/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { EventsService } from '@app/events/events/events.service';
import { Logger } from '@app/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Events } from '@app/events/events/events.model';
import { ApiQuery } from '@app/core/http/api-query';
import { EventType } from '@app/events/events/event-type.enum';
import { EventGroup } from '@app/events/events-groups/event-group.model';
import { IndustrySelectComponent } from '@app/shared/components/industry-select/industry-select.component';
import { VenueSelectComponent } from '@app/shared/components/venue-select/venue-select.component';
import { DatePickerComponent } from '@app/shared/components/date-picker/date-picker.component';
import { LaravelModelsEnum } from '@app/shared/models/model.model';

const log = new Logger('EventsFormComponent');

@Component({
  selector: 'app-events-form',
  templateUrl: './events-form.component.html',
  styleUrls: ['./events-form.component.scss'],
})
export class EventsFormComponent implements OnInit {
  @Input() event: Events = null;
  form: FormGroup;
  eventForFill: EventGroup = null;
  @ViewChild('industrySelectComponent', { static: true }) industrySelectComponent: IndustrySelectComponent;
  @ViewChild('venueSelectComponent', { static: true }) venueSelectComponent: VenueSelectComponent;
  @ViewChild('dateFromPicker', { static: true }) dateFromPicker: DatePickerComponent;
  @ViewChild('dateToPicker', { static: true }) dateToPicker: DatePickerComponent;
  EventType = EventType;
  LaravelModelsEnum = LaravelModelsEnum;

  constructor(public modal: NgbActiveModal, private formBuilder: FormBuilder, private eventService: EventsService) {}

  ngOnInit() {
    this.createForm();
    this.patchForm();
    this.typeChange();
  }

  save() {
    if (this.event && this.event.id) {
      const event: Events = this.event.deserialize(this.form.value);
      this.eventService.update(event).subscribe((res) => this.modal.close(res));
    } else {
      const event: Events = new Events().deserialize(this.form.value);
      this.eventService.store(event).subscribe((res) => this.modal.close(res));
    }
  }

  test(form: any) {
    log.debug(form);
  }

  /**
   * Променя валидацията на полетата спрямо type_id
   */
  typeChange(): void {
    log.debug('typeChange');
    this.form.get('type_id').valueChanges.subscribe((typeId: number) => {
      if (typeId === EventType.BUSINESS_TRIP) {
        this.form.get('venue_id').setValidators([]);
        this.form.get('venue_id').setValue(null);
        this.form.get('event_group_id').setValidators([]);
        this.form.get('event_group_id').setValue(null);
        this.form.get('city_id').setValidators([Validators.required]);

        this.form.get('organizer').setValue(null);
        this.form.get('visitors').setValue(null);
        this.form.get('exhibitors').setValue(null);
        this.form.get('industry_id').setValue(null);
        this.form.get('foreign_exhibitors').setValue(null);
        this.form.get('year_exhibitors').setValue(null);
        this.form.get('link_exhibitors').setValue(null);
        this.form.get('website_url').setValue(null);
      } else if (typeId === EventType.EVENT) {
        this.form.get('venue_id').setValidators([Validators.required]);
        this.form.get('event_group_id').setValidators([Validators.required]);
        this.form.get('city_id').setValidators([]);
        this.form.get('city_id').setValue(null);
      } else {
        this.form.get('venue_id').setValidators([]);
        this.form.get('venue_id').setValue(null);
        this.form.get('event_group_id').setValidators([]);
        this.form.get('event_group_id').setValue(null);
        this.form.get('city_id').setValidators([]);
        this.form.get('city_id').setValue(null);
      }

      this.form.get('venue_id').updateValueAndValidity();
      this.form.get('city_id').updateValueAndValidity();
      this.form.get('event_group_id').updateValueAndValidity();
    });
  }

  /**
   * Записва избрания EventGroup, за да може когато се натисне бутона да го fill-не
   * @param group Обекта с групата взет от event-group-select-a
   */
  eventGroupChange(group: EventGroup): void {
    this.eventForFill = null;
    if (group) {
      this.eventForFill = group;
    }
  }

  /**
   * Patch-ва формта от запазения EventGroup(this.eventForFill)
   * Налагаше се да направя set-ери на някои компоненти в противен случай няма как да им сетвам стойности многократно
   */
  patchFromEventGroup(): void {
    delete this.eventForFill.active;
    delete this.eventForFill.show_to_web;
    this.form.patchValue(this.eventForFill);

    this.industrySelectComponent.setData(this.eventForFill.industry);
    this.venueSelectComponent.setData(this.eventForFill.venue);
  }

  /**
   * Създава формата
   */
  private createForm(): void {
    this.form = this.formBuilder.group({
      id: [null],
      name: [null, [Validators.required]],
      note: [null],
      type_id: [null, [Validators.required]],
      organizer: [null],
      date_from: [null, [Validators.required]],
      date_to: [null, [Validators.required]],
      visitors: [null],
      city_id: [null],
      city: [null],
      exhibitors: [null],
      website_url: [null],
      foreign_exhibitors: [null],
      link_exhibitors: [null],
      year_exhibitors: [null],
      industry_id: [null],
      industry: [null],
      venue_id: [null],
      venue: [null],
      event_group_id: [null, [Validators.required]],
      event_group: [null],
      show_to_web: [false],
      active: [false],
      priority_id: [null],
      tags: [],
    });
  }

  /**
   * Patch-ва формата. Използва се да, за да попълни полетата на едит
   */
  private patchForm(): void {
    log.debug('patchForm');
    if (this.event && this.event.id) {
      const query = new ApiQuery().addIncludes('event_group', 'venue', 'industry', 'city.country', 'tags');
      this.eventService.show(this.event.id, query).subscribe((event: Events) => {
        // налага се да сетна value-то на type_id, за да може да се покажат зависимите от него полета преди да се patch-не
        this.form.get('type_id').setValue(event.type_id);
        setTimeout(() => {
          this.form.patchValue(event);
          // Refresh the date picker displays after form patching
          this.refreshDatePickers();
        });
      });
    } else if (this.event) {
      this.form.patchValue(this.event);
      // Refresh the date picker displays after form patching
      this.refreshDatePickers();
    }
  }

  /**
   * Refreshes the date picker displays to ensure they show the correct values
   */
  private refreshDatePickers(): void {
    setTimeout(() => {
      if (this.dateFromPicker) {
        this.dateFromPicker.refreshDisplay();
      }
      if (this.dateToPicker) {
        this.dateToPicker.refreshDisplay();
      }
    }, 100);
  }

}
